// Global Instructions Rule Applied!

import { FieldTypeEnum, SelectTypeEnum } from '../../../core/components/InputManager/types.ts'
import type { CountryChangePatch } from './types.ts'
import { LOOKUP_CATEGORY_SECURITY_TYPE } from '../../../state_manager/constants/lookupCategories.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY,
  RELATIONSHIP_CATEGORY_LEAD
} from '../../../state_manager/globals.ts'
import { ProspectFields, transformContactsToUserEntries } from '../../fundraising/utils/fieldConfigs.ts'

export function mapColumnToInputManagerField(column: any, record: any, helpers: any, fieldType: FieldTypeEnum): any {
  const {
    getCurrencyById,
    getLookupOptions,
    getPipelineStageOptionsForProspect,
    getRoleOptions,
    normalizedFundOptions,
    fundOptionsFooter,
    originalRecord,
    recordId
  } = helpers

  const columnKey = column.dataIndex || column.key
  const columnTitle = column.title || columnKey
  const isRequired = column.required || false
  const isColumnEditable = column.editable !== false
  const fallbackRecord = originalRecord || {}
  const effectiveRecordId = recordId || record.id || fallbackRecord.id || ''

  const getCurrentValue = () => {
    if (columnKey === 'role') {
      const roles = Array.isArray(record.prog__account_roles)
        ? record.prog__account_roles
        : Array.isArray(record[columnKey])
          ? record[columnKey]
          : []

      return roles
        .map((role: any) => {
          if (typeof role === 'string') return role
          if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
            return role.account_roles__account_role_choice_id
          }
          return null
        })
        .filter((id: any): id is string => id !== null)
    }

    return record[columnKey] !== undefined && record[columnKey] !== null ? record[columnKey] : fallbackRecord[columnKey] || ''
  }

  const value = getCurrentValue()

  const baseConfig: any = {
    key: `${effectiveRecordId}-${columnKey}`,
    label: columnTitle,
    value,
    required: isRequired,
    editable: isColumnEditable
  }

  switch (fieldType) {
    case FieldTypeEnum.CURRENCY:
      const currencyId = record[ProspectFields.prospects__currency] || record.currency || null
      return {
        ...baseConfig,
        type: FieldTypeEnum.CURRENCY,
        currencySymbol: getCurrencyById(currencyId)?.symbol || '$'
      }

    case FieldTypeEnum.SELECT:
      if (columnKey === ProspectFields.pipeline_prospects__stage) {
        const pipelineStageOptions = getPipelineStageOptionsForProspect(record)
        const selectFieldConfig: any = {
          ...baseConfig,
          type: FieldTypeEnum.SELECT,
          selectType: SelectTypeEnum.FILTER,
          options: pipelineStageOptions,
          allowClear: true,
          patchEndpoint: 'pipeline-prospects',
          patchFields: [
            { sourceKey: 'value', targetField: 'stage' },
            { sourceKey: 'stageDate', targetField: 'stage_date' }
          ],
          valueTransformer: (nextValue: any, currentRecord: any) => {
            const pipelineProspectsId =
              record?.pipeline_prospects__id || currentRecord?.pipeline_prospects__id || null
            const stageDate = new Date().toISOString().split('T')[0]
            const result: any = {
              value: nextValue,
              stageDate: stageDate,
              [ProspectFields.pipeline_prospects__stage]: nextValue,
              pipeline_prospects__stage_date: stageDate
            }
            if (pipelineProspectsId) {
              result.id = pipelineProspectsId
            }
            return result
          }
        }

        if (record[ProspectFields.pipeline_prospects__stage__label]) {
          selectFieldConfig.displayValue = record[ProspectFields.pipeline_prospects__stage__label]
        } else if (selectFieldConfig.options && selectFieldConfig.value) {
          const selectedOption = selectFieldConfig.options.find((opt: any) => opt.value === selectFieldConfig.value)
          if (selectedOption) {
            selectFieldConfig.displayValue = selectedOption.label
          }
        }

        return selectFieldConfig
      }

      if (columnKey === 'funds__name') {
        const fundValue = record[ProspectFields.prospects__fund] || record[columnKey] || ''
        return {
          ...baseConfig,
          key: `${effectiveRecordId}-${ProspectFields.prospects__fund}`,
          value: fundValue,
          type: FieldTypeEnum.SELECT,
          selectType: SelectTypeEnum.FILTER,
          options: normalizedFundOptions,
          // The gated picker must always offer a way to see what it hides
          // (fund status, 2026-09-04). Undefined when nothing is hidden.
          optionsFooter: fundOptionsFooter,
          allowClear: true
        }
      }

      if (columnKey === 'role') {
        return {
          ...baseConfig,
          key: `${effectiveRecordId}-accounts__roles`,
          type: FieldTypeEnum.SELECT,
          selectType: undefined,
          options: getRoleOptions ? getRoleOptions() : [],
          mode: 'multiple',
          tagDisplay: true,
          allowClear: true,
          patchEndpoint: 'accounts',
          valueTransformer: (nextValue: any, currentRecord: any) => {
            const newRoleIds = Array.isArray(nextValue) ? nextValue : []
            const currentRoles = Array.isArray(currentRecord?.prog__account_roles) ? currentRecord.prog__account_roles : []
            const currentRoleMap = new Map<string, any>()
            const newRoleSet = new Set(newRoleIds.filter((roleId: any) => !!roleId))

            currentRoles.forEach((role: any) => {
              if (typeof role === 'object' && role?.account_roles__account_role_choice_id) {
                currentRoleMap.set(role.account_roles__account_role_choice_id, role)
              } else if (typeof role === 'string') {
                currentRoleMap.set(role, { account_roles__account_role_choice_id: role })
              }
            })

            const rolesWithActions: any[] = []

            newRoleIds.forEach((roleChoiceId: string) => {
              if (!roleChoiceId) return
              const currentRole = currentRoleMap.get(roleChoiceId)
              if (currentRole?.account_roles__id) {
                rolesWithActions.push({
                  account_roles__id: currentRole.account_roles__id,
                  account_roles__account_role_choice_id: roleChoiceId,
                  prog__account_roles__action: 'update'
                })
              } else {
                rolesWithActions.push({
                  account_roles__id: crypto.randomUUID(),
                  account_roles__account_role_choice_id: roleChoiceId,
                  prog__account_roles__action: 'insert'
                })
              }
            })

            currentRoleMap.forEach((role: any, roleChoiceId: string) => {
              if (!newRoleSet.has(roleChoiceId) && role?.account_roles__id) {
                rolesWithActions.push({
                  account_roles__id: role.account_roles__id,
                  prog__account_roles__action: 'delete'
                })
              }
            })

            return {
              prog__account_roles: rolesWithActions
            }
          }
        }
      }

      let lookupCategory = ''
      if (columnKey === 'security_type') {
        lookupCategory = LOOKUP_CATEGORY_SECURITY_TYPE
      } else if (columnKey === 'accounts__business_country_code__label') {
        lookupCategory = 'Country'
      }
      return {
        ...baseConfig,
        type: FieldTypeEnum.SELECT,
        selectType: lookupCategory ? SelectTypeEnum.LOOKUP : undefined,
        lookupCategory: lookupCategory || undefined,
        options: lookupCategory ? getLookupOptions(lookupCategory) : []
      }

    case FieldTypeEnum.TEXT_ACTION:
    case FieldTypeEnum.LINK:
      return {
        ...baseConfig,
        type: fieldType,
        editable: false
      }

    case FieldTypeEnum.CITY:
      return {
        ...baseConfig,
        type: FieldTypeEnum.CITY,
        country: record.accounts__business_country_code || null,
        contextKey: `cell-${effectiveRecordId}-${columnKey}`,
        patchFields: [
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'cityName', targetField: 'business_city_label' },
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        valueTransformer: (cityObject: any) => {
          const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)
          const cityCode = isClearing ? null : (cityObject?.value ?? null)
          const cityLabel = isClearing ? null : cityObject?.name || null
          const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
          const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

          const result: Record<string, any> = {
            cityCode: cityCode,
            cityName: cityLabel,
            state: state,
            accounts__business_city_code__label: cityLabel,
            accounts__business_stateprovince: state
          }

          if (!isClearing && countryCode !== undefined) {
            result.countryCode = countryCode
            result.accounts__business_country_code = countryCode
          }

          return result
        }
      }

    case FieldTypeEnum.COUNTRY:
      return {
        ...baseConfig,
        type: FieldTypeEnum.COUNTRY,
        contextKey: `cell-${effectiveRecordId}-${columnKey}`,
        patchFields: [
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        valueTransformer: (countryObject: any): CountryChangePatch => {
          const isClearing =
            !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
          const countryCode = isClearing ? null : (countryObject?.value ?? null)
          const countryLabel = isClearing ? null : (countryObject?.label ?? null)

          return {
            countryCode: countryCode,
            cityCode: null,
            state: null,
            accounts__business_country_code: countryCode,
            accounts__business_country_code__label: countryLabel,
            accounts__business_city_code__label: null,
            accounts__business_stateprovince: null
          }
        }
      }

    case FieldTypeEnum.CONTACT:
      const contactArray = Array.isArray(record[columnKey]) ? record[columnKey] : record[columnKey] ? [record[columnKey]] : []

      const transformedContacts = contactArray.map((contact: any) => {
        if (typeof contact === 'string') {
          return { value: contact, label: contact }
        }
        const contactId = contact.contacts__id || contact.id || contact.value
        const displayName =
          contact.prog__display_name ||
          `${contact.contacts__first_name || ''} ${contact.contacts__last_name || ''}`.trim() ||
          contact.contacts__email ||
          contact.label ||
          'Unknown'
        return {
          value: contactId,
          label: displayName,
          ...contact
        }
      })

      let patchEndpoint = ''
      let relationshipCategoryId = ''
      let isContactProspects = false

      if (columnKey === ProspectFields.contact_prospects__lead) {
        patchEndpoint = 'contact_prospects'
        relationshipCategoryId = RELATIONSHIP_CATEGORY_LEAD
        isContactProspects = true
      } else if (columnKey === ProspectFields.contact_accounts__introduced_by) {
        patchEndpoint = 'contact_accounts'
        relationshipCategoryId = RELATIONSHIP_CATEGORY_INTRODUCED_BY
      } else if (columnKey === ProspectFields.contact_accounts__sourced_by) {
        patchEndpoint = 'contact_accounts'
        relationshipCategoryId = RELATIONSHIP_CATEGORY_SOURCED_BY
      }

      const originalContactArray = Array.isArray(fallbackRecord[columnKey])
        ? fallbackRecord[columnKey]
        : fallbackRecord[columnKey]
          ? [fallbackRecord[columnKey]]
          : []

      const previousContacts = originalContactArray.map((contact: any) => {
        if (typeof contact === 'string') {
          return { value: contact, label: contact }
        }
        const contactId = contact.contacts__id || contact.id || contact.value
        return { value: contactId, ...contact }
      })

      return {
        ...baseConfig,
        type: FieldTypeEnum.CONTACT,
        value: transformedContacts,
        mode: 'multiple',
        tagDisplay: column.tagDisplay || false,
        editable: isColumnEditable,
        patchEndpoint,
        valueTransformer: (nextValue: any, currentRecord: any) => {
          const currentContacts = Array.isArray(nextValue) ? nextValue : nextValue ? [nextValue] : []
          const userFields = transformContactsToUserEntries(
            currentContacts,
            previousContacts,
            relationshipCategoryId,
            isContactProspects
          )

          if (isContactProspects) {
            const prospectId =
              currentRecord?.[ProspectFields.prospects__id] ||
              currentRecord?.id ||
              record[ProspectFields.prospects__id] ||
              record.id ||
              fallbackRecord.id ||
              null
            return {
              id: prospectId,
              userFields: userFields || [],
              relationship_category_id: relationshipCategoryId
            }
          }

          const accountId =
            currentRecord?.[ProspectFields.prospects__investor] ||
            currentRecord?.accounts__id ||
            record[ProspectFields.prospects__investor] ||
            record.accounts__id ||
            null
          return {
            id: accountId,
            userFields: userFields || [],
            relationship_category_id: relationshipCategoryId
          }
        }
      }

    default:
      return {
        ...baseConfig,
        type: FieldTypeEnum.TEXT
      }
  }
}
